import { useContext } from 'react'
import { Auth } from 'aws-amplify'
import { toast } from 'react-toastify'
import { dowOptions } from '@src/utility/enum'
import { ThemeColors } from '@src/utility/context/ThemeColors'
import { Capacitor } from '@capacitor/core'
import { App as CapApp } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import Toast from '@comps/Toast'
import moment from 'moment'
import '@styles/base/bootstrap-extended/_variables.scss'

// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = obj => Object.keys(obj).length === 0

// ** Returns K format from a number
export const kFormatter = num => (num > 999 ? `${(num / 1000).toFixed(1)}k` : num)

// ** Converts HTML to string
export const htmlToString = html => html.replace(/<\/?[^>]+(>|$)/g, '')

export const isToday = date => {
  const today = new Date()
  return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()
}

export const isTodayEvening = date => {
  const today = new Date()
  return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear() && ((today.getHours() * 60) + today.getMinutes()) > 1290
}

export const formatDate = (value, formatting = { month: 'long', day: 'numeric', year: 'numeric' }) => {
  if (!value) return value
  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value))
}

// ** Returns short month of passed date
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
  const date = new Date(value)
  let formatting = { month: 'short', day: 'numeric' }

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: 'numeric', minute: 'numeric' }
  }

  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value))
}

const dec2hex = (dec) => {
  return dec.toString(16).padStart(2, '0')
}

export const randomString = (len = 10) => {
  const arr = new Uint8Array((len || 40) / 2)
  window.crypto.getRandomValues(arr)
  return Array.from(arr, dec2hex).join('')
}

export const getJWToken = () => {
  let jwt = null
  const udKey = localStorage.getItem('userDataKey')
  if (udKey) {
    const idKeyArr = udKey.split('.')
    idKeyArr[3] = 'idToken'
    jwt = localStorage.getItem(idKeyArr.join('.'))
  }
  return jwt
}

export const decodeJwtPayload = token => {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`
    }).join(''))

    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('decodeJwtPayload error:', error)
    return null
  }
}

export const setGlobalOrg = id => localStorage.setItem('globalOrg', id)

export const getGlobalOrg = () => (localStorage.getItem('globalOrg') ? Number(localStorage.getItem('globalOrg')) : getJWToken() ? decodeJwtPayload(getJWToken())['custom:org_id'] : null)

export const setInternalOrg = id => sessionStorage.setItem('internalOrg', id)

export const getInternalOrg = () => (sessionStorage.getItem('internalOrg') ? Number(sessionStorage.getItem('internalOrg')) : null)

export const clearInternalOrg = () => sessionStorage.removeItem('internalOrg')

export const setUserDataKey = user => {
  if (user?.userDataKey) {
    localStorage.setItem('userDataKey', user?.userDataKey)
    const idKeyArr = user?.userDataKey.split('.')
    idKeyArr[3] = 'idToken'
    const jwt = localStorage.getItem(idKeyArr.join('.'))
    const orgId = decodeJwtPayload(jwt)['custom:org_id']
    if (orgId) setGlobalOrg(orgId)
  }
}

export const removeUserData = () => {
  localStorage.removeItem('userDataKey')

  const delKeys = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key.startsWith('CognitoIdentityServiceProvider')) delKeys.push(key)
  }

  delKeys.forEach(key => localStorage.removeItem(key))
}

export const handleLogout = () => {
  try {
    removeUserData()
    Auth.signOut()
  } catch (error) { console.log('error signing out: ', error) }
  window.location = '/home'
}

export const getUserData = () => getJWToken() && decodeJwtPayload(getJWToken())
export const getIDTokenName = () => getJWToken() && decodeJwtPayload(getJWToken()).name
export const getIDTokenPhone = () => getJWToken() && decodeJwtPayload(getJWToken()).phone_number.substring(3)
export const getIDTokenEmail = () => getJWToken() && decodeJwtPayload(getJWToken())['custom:EmailAddress']
export const getUserGroups = () => getJWToken() && decodeJwtPayload(getJWToken())['cognito:groups']
export const getIDTokenSub = () => getJWToken() && decodeJwtPayload(getJWToken()).sub

export const isSuperAdmin = () => getUserGroups() && getUserGroups().includes('superadmin')
export const isSpAdmin = () => getUserGroups() && getUserGroups().includes('spadmin')
export const isOrgAdmin = () => getUserGroups() && getUserGroups().includes('orgadm')
export const isVenueAdmin = () => getUserGroups() && getUserGroups().includes('venueadm')
export const isFinAdmin = () => getUserGroups() && getUserGroups().includes('finadm')
export const isMarker = () => getUserGroups() && getUserGroups().includes('marker')
export const isCoach = () => getUserGroups() && getUserGroups().includes('coach')

export const refreshMySession = async () => {
  try {
    if (getJWToken() && new Date(decodeJwtPayload(getJWToken()).exp * 1000) < new Date()) {
      const user = await Auth.currentAuthenticatedUser()
      setUserDataKey(user)
      toast.warning(<Toast title='Refreshing session ...' />, {
        toastId: 'refresh-session',
        autoClose: 3000,
        hideProgressBar: false,
        closeButton: true
      })
    }
    return true
  } catch (e) {
    console.error('error refreshing token, err: ', e)
    handleLogout()
    toast.error(<Toast title='Session exipred, kindly re-login...' />, {
      toastId: 'session-expired-2',
      autoClose: 4000,
      hideProgressBar: false,
      closeButton: false
    })
    return false
  }
}

export const toIsoString = (dt) => {
  const date = new Date(dt)
  const tzo = -date.getTimezoneOffset(),
    dif = tzo >= 0 ? '+' : '-',
    pad = (num) => {
      const norm = Math.floor(Math.abs(num))
      return (norm < 10 ? '0' : '') + norm
    }

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${dif}${pad(tzo / 60)}:${pad(tzo % 60)}`
}

export const toDateString = (date) => {
  const pad = (num) => {
    const norm = Math.floor(Math.abs(num))
    return (norm < 10 ? '0' : '') + norm
  }
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export const getLongDate = (d) => moment(d).format('ddd, Do MMM')

export const formatTime = (dateString) => {
  const date = new Date(dateString)
  let hours = date.getHours()
  let minutes = date.getMinutes()
  const ampm = hours >= 12 ? 'pm' : 'am'
  hours = hours % 12
  hours = hours ? hours : 12
  minutes = minutes < 10 ? `0${minutes}` : minutes
  const strTime = `${hours}:${minutes}${ampm}`
  return strTime
}

export const getDateTime = (dateString) => {
  const date = new Date(dateString)
  const str = date.toLocaleDateString('default', { year: 'numeric', month: 'short', day: 'numeric' })
  return `${formatTime(dateString)} ${str}`
}


// ** react select styles
export const selectStyles = (error = false) => ({
  control: (styles, { isFocused, isDisabled }) => ({
    ...styles,
    backgroundColor: '#f6f6f6',
    border: `1px solid ${error ? '#ea5455' : isDisabled ? '#166512' : isFocused ? '#166512' : '#166512'}`,
    boxShadow: isFocused ? 'none' : styles.boxShadow,
    '&:hover': { borderColor: '#166512', cursor: 'pointer' },
    cursor: 'default',
    opacity: isDisabled ? 0.5 : 1
  }),
  indicatorSeparator: (styles) => ({ ...styles, display: 'none', backgroundColor: '#166512' }),
  indicator: (styles) => ({ ...styles, cursor: 'pointer', svg: { cursor: 'pointer' } }),
  singleValue: (styles) => ({ ...styles, color: '#6e6b7b' }),
  placeholder: (styles) => ({ ...styles, color: '#6e6b7b' }),
  menu: (styles) => ({ ...styles, backgroundColor: '#f6f6f6', zIndex: 3 }),
  menuList: (styles) => ({ ...styles, backgroundColor: '#f6f6f6', zIndex: 3 }),
  option: (styles, { data, isDisabled, isFocused, isSelected, isActive }) => ({
    ...styles,
    cursor: 'pointer',
    backgroundColor: isFocused || isActive ? `rgba(${'16,124,15'}, 0.4)` : isSelected ? '#166512' : '#f6f6f6',
    color: isSelected ? 'white' : isDisabled ? '#676d7d' : '#6e6b7b'
  }),
  group: (styles) => ({ ...styles }),
  groupHeading: (styles) => ({ ...styles, marginBottom: '0.5rem', color: '#d0d2d6', fontWeight: 'bolder', fontSize: 'inherit' }),
  multiValue: (styles) => ({ ...styles, backgroundColor: `rgba(${'16,124,15'}, 0.4)`, borderRadius: '3px', margin: '0 0.7rem 0 0' }),
  multiValueLabel: (styles) => ({ ...styles, color: '#6e6b7b', fontSize: '0.85rem', borderRadius: '0.357rem', padding: '0.26rem 0.6rem' }),
  multiValueRemove: (styles) => ({
    ...styles,
    paddingLeft: 0,
    paddingRight: '0.5rem',
    ':hover': { backgroundColor: 'inherit', color: 'inherit' },
    svg: { height: '0.85rem', width: '0.85rem', ':hover': { cursor: 'pointer' } }
  })
})

export const convertToCSV = (objArray) => {
  const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray
  let str = ''

  for (let i = 0; i < array.length; i++) {
    let line = ''
    for (const index in array[i]) {
      if (line !== '') line += ','

      line += array[i][index]
    }
    str += `${line}\r\n`
  }

  return str
}

export const exportCSVFile = (headers, items, fileTitle) => {
  if (headers) {
    items.unshift(headers)
  }

  const csv = convertToCSV(items)

  const exportFilename = `${fileTitle.replaceAll(' ', '_')}.csv` || 'export.csv'

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  if (navigator.msSaveBlob) { // IE 10+
    navigator.msSaveBlob(blob, exportFilename)
  } else {
    const link = document.createElement('a')
    if (link.download !== undefined) { // feature detection
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', exportFilename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}

export const makeLabelVals = (d) => {
  const arr = []
  d.map((val) => { if ((val.visible || val.visible === undefined)) arr.push({ label: val.name, value: val.name }) })
  return arr
}

export const makeIdTitles = (d) => {
  const arr = []
  d.map((val) => { if (val.visible) arr.push({ id: val.name, title: val.name }) })
  return arr
}

export const toTime = (min) => {
  if (isNaN(min)) return 'NA'
  const date = new Date(0)
  date.setMinutes(min + 30)
  const minStr = date.toISOString().substring(11, 16)
  return minStr
}

export const fromTime = (minStr) => {
  if (minStr.isEmpty || minStr.length !== 5) return 0
  let min = 0
  min += Number(minStr.substring(0, 2)) * 60
  min += Number(minStr.substring(3, 5))
  return min
}

export const capFirst = (s) => s.charAt(0).toUpperCase() + s.toLowerCase().slice(1)

export const mobileCheck = function () {
  let check = false
  if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(navigator.userAgent || navigator.vendor || window.opera) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test((navigator.userAgent || navigator.vendor || window.opera).substr(0, 4))) check = true
  return check
}

export function getWeekdays(n) {
  let dow = ''
  const days = ['M', 'T', 'W', 'Th', 'F', 'S', 'Su']
  if (n) for (let i = 0; i < n.length; i++) {
    const index = parseInt(n[i])
    if (index >= 0 && index < days.length) dow += days[index]
  }
  return dow
}

export const isUrlImage = (url) => {
  const img = new Image()
  img.src = url
  return new Promise((resolve) => {
    img.onload = () => resolve(true)
    img.onerror = () => resolve(false)
  })
}

export const isUrlFile = async (url) => {
  let isFile = false
  await fetch(url, { method: 'GET' }).then(() => { isFile = true }).catch(() => { isFile = false })
  return isFile
}

export const isOfAge = d => moment().isAfter(moment(d).add(18, 'Y'))

export const mDate = d => moment(d, 'YYYY-MM-DD').format('DD MMM')

export const myDate = d => moment(d, 'YYYY-MM-DD').format('DD MMM YYYY')

export const rgMDate = (s, e) => `${moment(s, 'YYYY-MM-DD').format('DD MMM')} - ${moment(e, 'YYYY-MM-DD').format('DD MMM YYYY')}`

export const rgMYDate = (s, e) => `${moment(s, 'YYYY-MM-DD').format('DD MMM YYYY')} - ${moment(e, 'YYYY-MM-DD').format('DD MMM YYYY')}`

export const toDateStr = d => d.format('YYYY-MM-DD')

export const str2Float = s => parseFloat(`${s}`.replaceAll(',', ''))

export const uiAmount = num => (Number.isInteger(Number.parseFloat(num.toFixed(3))) ? `₹ ${num.toLocaleString()}` : `₹ ${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)

export const uiTime = dt => moment(dt).format('hh:mm a')

export const uiPercent = num => `${num.toFixed(2)}%`

export const rowHoverStyle = { rows: { highlightOnHoverStyle: { backgroundColor: 'rgba(40, 199, 111, 0.12) !important' } } }

export const slimTime = (t) => t.replace(' to ', '-').replaceAll(':00', '').replaceAll('-0', '-').replace(/^0+/, '')

export const uiTimings = (s) => {
  const times = s.reduce((ids, i) => (ids.includes(i.timings.replace(' to ', '-')) ? ids : [...ids, i.timings.replace(' to ', '-')]), [])
  return times.map(t => `${getWeekdays(s.filter(a => a.timings.replace(' to ', '-') === t).reduce((ids, i) => [...ids, i.dow], []).sort().join(''))} / ${slimTime(t)}`)
}

export const getFlexiSessions = (s) => {
  const fs = []
  s.map(sl => { fs.push({ value: sl.id, label: `${dowOptions.find(op => op.value === sl.dow).label.substring(0, 3)} : ${slimTime(sl.timings)}`, dow: sl.dow }) })
  return fs
}

export const renderGender = (g) => {
  if (g === 'M') return 'Male'
  else if (g === 'F') return 'Female'
  else if (g === 'B') return 'Female & Male'
  else return 'unknown'
}

export const jsonVal = (json, key) => (json && key && JSON.parse(json)[key]) || ''

export const getMonths = p => {
  const pt = p.toLowerCase()
  if (pt.indexOf('monthly') > -1) return 1
  if (pt.indexOf('quarterly') > -1) return 3
  if (pt.indexOf('halfyearly') > -1) return 6
  if (pt.indexOf('annual') > -1) return 12
  else return 1
}

export const mod5 = num => {
  return num < 0 ? (3 + num) % 5 : num % 5
}

export const barColor = light => {
  const { colors } = useContext(ThemeColors)
  return light ? colors.primary.light : colors.primary.main
}

export const barColors = light => {
  const { colors } = useContext(ThemeColors)
  return light ? [colors.warning.light, colors.info.light, colors.success.light, colors.danger.light, colors.dark.light] : [colors.warning.main, colors.info.main, colors.success.main, colors.danger.main, , colors.dark.main]
}

export const getDevice = () => {
  const ua = navigator.userAgent
  const pl = navigator.platform
  if (ua.includes('Android')) return 'Android'
  if (ua.includes('iPad') || (pl === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'iPad'
  if (pl === 'iPhone' || pl === 'iPod' || (ua.includes('iPhone') && !ua.includes('Android'))) return 'iPhone'
  return 'Other'
}

export const url4app = s => {
  return (!s || s === '') ? '' : s.startsWith('capacitor') ? s.replace('capacitor://localhost', 'https://www.shareplay.in') : s
}

// Reversible obfuscation for ids in shared links — makes /pEn tokens look opaque (cosmetic, not security)
const ENROL_KEY = 'sp697Renew'
export const encEnrolId = id => {
  const s = String(id)
  let out = ''
  for (let i = 0; i < s.length; i++) out += String.fromCharCode(s.charCodeAt(i) ^ ENROL_KEY.charCodeAt(i % ENROL_KEY.length))
  return btoa(out).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
export const decEnrolId = token => {
  try {
    const raw = atob(token.replace(/-/g, '+').replace(/_/g, '/'))
    let out = ''
    for (let i = 0; i < raw.length; i++) out += String.fromCharCode(raw.charCodeAt(i) ^ ENROL_KEY.charCodeAt(i % ENROL_KEY.length))
    const n = Number(out)
    return Number.isNaN(n) ? -1 : n
  } catch (e) {
    return -1
  }
}

const compareVer = (cur, req) => {
  const c = cur.split('.').map(Number)
  const r = req.split('.').map(Number)
  for (let i = 0; i < r.length; i++) {
    if ((c[i] || 0) > r[i]) return true
    if ((c[i] || 0) < r[i]) return false
  }
  return true
}

export const checkAppVer = async (minVer) => {
  try {
    const pf = Capacitor.getPlatform()

    if (pf !== 'web') {
      const appInfo = await CapApp.getInfo()
      if (!compareVer(appInfo.version, minVer[pf])) {
        toast.warning(<Toast title={`The latest version of SharePlay app is required. Please update from ${pf === 'android' ? 'PlayStore' : 'AppStore'}.`} />, {
          autoClose: 3000,
          hideProgressBar: false,
          closeButton: true
        })
        setTimeout(async () => {
          if (pf === 'android') window.location.href = 'https://play.google.com/store/apps/details?id=com.app.shareplay'
          else if (pf === 'ios') await Browser.open({ url: 'https://apps.apple.com/in/app/shareplay-in/id6740692596' })
        }, 3000)
      }
    }
  } catch (error) {
    console.error('Version check failed:', error)
    toast.warning(<Toast title='Error checking app version, ensure you have the latest shareplay app' />, {
      autoClose: 3000,
      hideProgressBar: false,
      closeButton: true
    })
  }
}